import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveComWixpressHtmlCacheInvalidateInvalidateCacheServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'api._api_base_domain_': [
      {
        srcPath: '/wix-public-html-cache-invalidate-webapp',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath:
          '/_api/wix-public-html-cache-invalidate-webapp/v1/purge-site-cache',
        destPath: '/v1/purge-site-cache',
      },
      {
        srcPath:
          '/_api/wix-public-html-cache-invalidate-webapp/invalidate-cache',
        destPath: '/v1/invalidate-cache',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ssr/v1/invalidate-cache',
        destPath: '/v1/invalidate-cache',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/ssr/v1/invalidate-cache',
        destPath: '/v1/invalidate-cache',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_cache_cache';

/**
 * <blockquote class="important">
 *
 * __Important:__
 * This API is only supported for developing sites and works only when used together with the Web Methods or Router APIs.
 *
 * </blockquote>
 *
 * Invalidates or clears previously cached return values based on specified tags.
 *
 * The `invalidationMethods` parameter accepts an array of objects, each containing a `tag` field. These tags are defined when caching return values of site [web methods](https://dev.wix.com/docs/sdk/core-modules/web-methods/introduction) and [routers](https://dev.wix.com/docs/velo/events-service-plugins/routers/service-plugins/wix-router/introduction#events-service-plugins_routers_service-plugins_wix-router_router-caching).
 *
 * For example, an array such as `[{ tag: "contacts" }, { tag: "labels" }]` will clear any cached return values tagged with "contacts" or "labels."
 *
 * Any cache containing at least one tag specified in the `invalidationMethods` parameter will be cleared.
 *
 * >**Notes:**
 * >- If you don't specify any tags in the `invalidationMethods` parameter, no caches are invalidated.
 * >- If you don't call Invalidate Cache, caches are only invalidated when the Time to Live (TTL) expires or when the site is republished with a code change.
 * >- This method does not invalidate the Server Side Rendering (SSR) cache for your site. To invalidate that cache, use the Invalidate Cache method from the [`wix-site-backend`](https://dev.wix.com/docs/velo/api-reference/wix-site-backend/invalidate-cache) module.
 */
export function invalidateCache(payload: object): RequestOptionsFactory<any> {
  function __invalidateCache({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ssr.v1.invalidate_cache_request',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.html.cache.invalidate.InvalidateCacheService.invalidateCache',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressHtmlCacheInvalidateInvalidateCacheServiceUrl({
        protoPath: '/v1/invalidate-cache',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __invalidateCache;
}

/**
 * Invalidates or clears previously cached return values based on metaSiteID.
 * Takes the metaSiteID from the context and performs a hard purge.
 */
export function purgeSiteCache(payload: object): RequestOptionsFactory<any> {
  function __purgeSiteCache({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ssr.v1.invalidate_cache_request',
      method: 'POST' as any,
      methodFqn:
        'com.wixpress.html.cache.invalidate.InvalidateCacheService.PurgeSiteCache',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveComWixpressHtmlCacheInvalidateInvalidateCacheServiceUrl({
        protoPath: '/v1/purge-site-cache',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __purgeSiteCache;
}
